import CRMStatusChip from '@components/Common/CRMStatusChip';
import { SectionCard } from '@components/Common/EntityWorkspace';
import EmptyState from '@components/Common/EmptyState';
import ErrorMessage from '@components/Common/ErrorMessage';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import type { HrUser } from '@components/Common/Utils/useHrUsers';
import { resolveStatus, stageColor, stagesFor } from '@constants/applicationStatus';
import { jobApplicantsList } from '@constants/graphQLQueries';
import { candidateDisplayName } from '@helpers/candidateRecord';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { Box, Button, Chip } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './PipelineTab.scss';
import { formatDate, formatDayMonth } from '@helpers/date';

interface Row {
  id: string;
  name: string;
  stage: string;
  /** Detail, or for a Closed record its reason. */
  detail: string;
  reason: string;
  ownerId: string;
  nextAction: string;
  nextActionAt: string;
  updatedAt: string;
}

/** Everything on one job, which is what this tab is for; the list page pages past this. */
const MAX_ROWS = 500;

/**
 * One job's candidates, by stage (spec section 6).
 *
 * The counts are the filter: click a stage to list only its candidates, and click Closed's reasons
 * to see why people left. The counts are taken from the same rows the list shows, so the two can
 * never disagree. When the joined count reaches the openings on an Active job, the tab offers to
 * mark it Filled; it never does so itself.
 */
const PipelineTab: React.FC<{
  jobId: string;
  managed: boolean;
  numberOfPositions: number | null;
  requirementStatus: string;
  hrUsers: HrUser[];
  /** The full applications list, filtered to this job. */
  listLink: string;
  onAddCandidate: () => void;
  onMarkFilled: () => void;
}> = ({ jobId, managed, numberOfPositions, requirementStatus, hrUsers, listLink, onAddCandidate, onMarkFilled }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState('');
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await fetchGraphQLApiData(
        jobApplicantsList(0, MAX_ROWS, [`{ key: "jobId", value: ${JSON.stringify(jobId)} }`]),
      );
      const content = data?.getAllJobApplications?.content ?? [];
      setTotal(data?.getAllJobApplications?.totalElements ?? content.length);
      setRows(
        content.filter(Boolean).map((a: any) => {
          const r = resolveStatus(a.status, a.subStatus, a.closedReason);
          const label = r.stage?.label ?? '';
          return {
            id: a._id,
            name: candidateDisplayName(a, 'Candidate'),
            stage: label,
            detail: label === 'Closed' ? r.closedReason : r.detail && r.detail !== label ? r.detail : '',
            reason: label === 'Closed' ? r.closedReason || 'No reason recorded' : '',
            ownerId: a.ownerId || '',
            nextAction: a.nextAction || '',
            nextActionAt: a.nextActionAt || '',
            updatedAt: a.updatedAt || a.createdAt || '',
          };
        }),
      );
    } catch {
      setError('Could not load this job’s candidates.');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  const stages = useMemo(() => {
    const own = stagesFor(managed).map((s) => s.label);
    // A record from before the workflow can sit on a stage the job no longer uses; count it.
    const extra = Array.from(new Set(rows.map((r) => r.stage))).filter((s) => s && !own.includes(s));
    return [...own.filter((s) => s !== 'Closed'), ...extra, 'Closed'];
  }, [managed, rows]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    rows.forEach((r) => { c[r.stage] = (c[r.stage] ?? 0) + 1; });
    return c;
  }, [rows]);

  const reasons = useMemo(() => {
    const c: Record<string, number> = {};
    rows.filter((r) => r.stage === 'Closed').forEach((r) => { c[r.reason] = (c[r.reason] ?? 0) + 1; });
    return Object.entries(c).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const shown = rows.filter((r) => (!stage || r.stage === stage) && (!reason || r.reason === reason));

  const joined = counts.Joined ?? 0;
  const offerFilled = !!numberOfPositions && joined >= numberOfPositions && requirementStatus === 'Active';

  const pick = (s: string) => {
    setReason('');
    setStage((cur) => (cur === s ? '' : s));
  };

  const ownerName = (id: string) => hrUsers.find((u) => u._id === id)?.name ?? '';

  if (error) return <ErrorMessage message={error} />;
  if (loading) return <SkeletonLoader />;

  return (
    <SectionCard
      title="Pipeline"
      icon={<GroupsOutlinedIcon />}
      meta={`${rows.length} candidate${rows.length === 1 ? '' : 's'}${numberOfPositions ? ` · ${joined} of ${numberOfPositions} joined` : ''}`}
    >
      <Box className="jp-pipeline cc-field--full">
        {offerFilled && (
          <Box className="jp-pipeline__prompt" role="status">
            <span>
              All {numberOfPositions} opening{numberOfPositions === 1 ? ' is' : 's are'} filled. Mark the job Filled to take it off the
              listings?
            </span>
            <Button size="small" variant="contained" onClick={onMarkFilled}>Mark as Filled</Button>
          </Box>
        )}

        {/* The counts are the filter. */}
        <Box className="jp-pipeline__stages" role="group" aria-label="Filter by stage">
          {stages.map((s) => {
            const active = stage === s;
            const sx = stageColor(s);
            return (
              <Chip
                key={s}
                label={`${s} ${counts[s] ?? 0}`}
                onClick={() => pick(s)}
                aria-pressed={active}
                variant={active ? 'filled' : 'outlined'}
                sx={active ? { backgroundColor: sx.backgroundColor, color: sx.color } : undefined}
              />
            );
          })}
        </Box>

        {stage === 'Closed' && reasons.length > 0 && (
          <Box className="jp-pipeline__reasons" role="group" aria-label="Filter closed candidates by reason">
            {reasons.map(([r, n]) => (
              <Chip
                key={r}
                size="small"
                label={`${r} ${n}`}
                onClick={() => setReason((cur) => (cur === r ? '' : r))}
                aria-pressed={reason === r}
                variant={reason === r ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        )}

        <Box className="jp-pipeline__actions">
          <Button size="small" variant="outlined" onClick={onAddCandidate}>
            {managed ? 'Add candidate at Sourcing' : 'Add candidate'}
          </Button>
          <Button size="small" component={Link} to={listLink}>Open in the applications list</Button>
          {total > rows.length && (
            <span className="jp-pipeline__cap">Showing the {rows.length} most recent of {total}.</span>
          )}
        </Box>

        {shown.length === 0 ? (
          <EmptyState
            compact
            title={rows.length === 0 ? 'No candidates on this job yet' : 'Nobody at this stage'}
          />
        ) : (
          <MintlyTable
            rows={shown}
            pagination={{ enabled: false, page: 0, rowsPerPage: shown.length || 1, totalNumberOfRows: shown.length, handleChangePage: () => {} }}
            // The workspace's centre column is ~480px: four columns fit, six truncated every header.
            // The next action and dates are one click away, on the candidate.
            selectors={managed ? ['name', 'stage', 'detail', 'owner'] : ['name', 'stage', 'detail']}
            tableHeadRow={{
              name: <>Candidate</>,
              stage: <>Stage</>,
              detail: <>Detail or reason</>,
              owner: <>Recruiter</>,
              nextAction: <>Next action</>,
              updatedAt: <>Updated</>,
            }}
            tableBodyRow={{
              name: (r: Row) => <>{r.name}</>,
              stage: (r: Row) => <CRMStatusChip status={r.stage} kind="application" />,
              detail: (r: Row) => <>{r.detail || '-'}</>,
              owner: (r: Row) => <>{ownerName(r.ownerId) || '-'}</>,
              nextAction: (r: Row) => (
                <>{r.nextAction ? `${r.nextAction}${r.nextActionAt ? ` (${formatDayMonth(r.nextActionAt)})` : ''}` : '-'}</>
              ),
              updatedAt: (r: Row) => <>{formatDate(r.updatedAt)}</>,
            }}
            onRowClick={(r: Row) => navigate(`/job-applications/${r.id}`)}
          />
        )}
      </Box>
    </SectionCard>
  );
};

export default PipelineTab;
